/**
 * Loading a micro-app into the console (LO-31).
 *
 * The console is a portal: the kernel says which instances are provisioned
 * (`GET /instances`), and opening one means loading its entry module and
 * handing it a `<div>`. The public description of this is
 * `docs/remote-contract.md`, which is what a micro-app author reads; this file
 * is the host half of the same contract.
 *
 * ## Plain ES modules, not Module Federation
 *
 * Decided. Federation couples the host's build to the remote's and negotiates
 * shared dependencies at load time — a lot of machinery for a host whose whole
 * job is loading remotes it was **not built alongside**. A provisioned instance
 * may be built by anyone, with any bundler, long after this console shipped.
 * `import()` is the browser's own contract and does not care.
 *
 * ## Three failures, three sentences
 *
 * A portal that renders "could not load app" for all of these is a portal that
 * makes somebody open devtools to find out which of three completely different
 * things went wrong. So the phase is carried on the error:
 *
 * - `load` — the module never arrived. Usually CORS, a 404, or a syntax error
 *   in the remote's own bundle.
 * - `contract` — it arrived and is not a remote. No `mount` export.
 * - `mount` — it is a remote and it threw on the way up. The remote's bug,
 *   and the one case where the message is worth showing verbatim.
 */

import { hostConfig } from "./client.js";

export const CONTRACT = 1;
/** Bumped only if the shape of `context` changes incompatibly. A remote may
 *  read it and refuse a host it does not understand. */

export class RemoteError extends Error {
  constructor(phase, message, cause) {
    super(message);
    this.phase = phase;
    this.cause = cause;
  }
}

/**
 * Fetch and validate a remote's entry module.
 *
 * `@vite-ignore` because the URL comes from the kernel at runtime. Without it
 * Vite tries to resolve and bundle the import at build time, which cannot work
 * — the whole premise is that the host does not know its remotes.
 */
export async function loadRemote(url) {
  let module;
  try {
    module = await import(/* @vite-ignore */ url);
  } catch (cause) {
    throw new RemoteError(
      "load",
      // Named rather than paraphrased. A browser says "Failed to fetch
      // dynamically imported module" for CORS, DNS, 404 and a parse error
      // alike, so the URL is the only part of this a reader can act on.
      `could not load ${url}`,
      cause,
    );
  }

  if (typeof module?.mount !== "function") {
    throw new RemoteError(
      "contract",
      `${url} loaded, but it does not export a mount() function — so it is a ` +
        `module, not a micro-app`,
    );
  }
  return module;
}

/**
 * What a mounted remote is told about where it is running.
 *
 * ## There is no token in here, and that is the entire point
 *
 * `effective = manifest ∩ user`. Which app a request comes from is decided by
 * the Cognito app client the token was signed for, which is not assertable by
 * the caller — that is what makes the manifest mean anything.
 *
 * The console's manifest is the widest in the system: it declares every type
 * because its job is to show what the kernel holds. So a console that passed
 * its access token to a mounted remote would be handing over its *manifest*.
 * Every micro-app in the portal would read everything the console can, and the
 * Apps screen — the one screen built to make ceilings visible — would show the
 * console's ceiling while the remote quietly enjoyed it. A governance hole in
 * the product whose purpose is preventing governance holes.
 *
 * So the remote authenticates as itself: its own PKCE flow, same pool, its own
 * `client_id`, through `/remote-callback.html`. The user already has a session
 * with the pool, so in the normal case nothing is shown and nobody is asked —
 * but it is a real second authorization and the token carries the remote's
 * identity, not ours.
 *
 * Which is why this object carries no token, and no `fetch` wrapper either: a
 * wrapper holding the console's Authorization header is the same hole with an
 * extra function around it.
 *
 * `instance` is the remote's Cognito app client id — the kernel keys the
 * pointer on it, so the identity the kernel governs it by and the identity it
 * signs in as are the same string, and neither side has to be told twice.
 */
export function mountContext({ instance, appType, colorScheme, signOut }) {
  const C = hostConfig();
  return {
    contract: CONTRACT,
    api: C.api,
    login: C.login,
    instance,
    appType,
    // The console's origin, because that is where the remote is running and
    // therefore the only origin Cognito can send its code back to. The page
    // is forty lines that postMessage the code to its opener, same-origin.
    redirectUri: `${window.location.origin}/remote-callback.html`,
    // The value now. The element carries `data-color-scheme` and the host
    // keeps that current, so a remote that styles off the attribute follows
    // the console live — including the default "system" state.
    colorScheme,
    signOut,
  };
}

/**
 * Put a remote into an element, and give back the function that takes it out.
 *
 * Separated from React so the ordering is readable on its own. The cleanup is
 * the half that goes wrong: it must be safe to call before `mount` finished,
 * after it failed, and twice.
 */
export function teardown(module, element) {
  if (typeof module?.unmount === "function") {
    try {
      module.unmount(element);
    } catch {
      // A remote that throws on the way out has still gone. Rethrowing here
      // would take the console down with it, and the user has already
      // navigated away from the thing that failed.
    }
  }
  // The backstop. The contract asks a remote to clean up after itself, and
  // this is what makes a remote that does not fail to leak into the *next*
  // mount rather than merely leaking. It only covers DOM inside the element —
  // listeners the remote attached to `window` are its own to remove, which is
  // why the contract says so out loud.
  element.replaceChildren();
}
